import { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { GetVariables } from "../../../services/api/Listagens/GetVariables";
import { PostListagens } from "../../../services/api/Listagens/PostListagens";
import { getTokenData, notify } from "../../../shared/utils/utils";

import {
  buildVariablesByCategory,
  flattenVariables,
  normalize,
} from "../utils/sheetsAdd.utils";

export default function useSheetsAddController({
  setAlreadyFetchSheets,
}) {
  const [padronizationPage, setPadronizationPage] = useState(0);
  const [dataR, setDataR] = useState([]);
  const [variablesOfTemplate, setVariablesOfTemplate] = useState({});
  const [loadingCheck, setLoadingCheck] = useState(false);

  /* =========================
     Flatten das variáveis
  ========================= */
  const allVariables = useMemo(
    () => flattenVariables(variablesOfTemplate),
    [variablesOfTemplate]
  );

  /* =========================
     Carrega variáveis do sistema
  ========================= */
  useEffect(() => {
    async function fetchVariables() {
      try {
        const resultJson = await GetVariables();
        const grouped = buildVariablesByCategory(resultJson?.data || []);
        setVariablesOfTemplate(grouped);
      } catch (err) {
        console.error(err);
      }
    }

     fetchVariables();
  }, []);

  /* =========================
     Step 1: Checa planilha
  ========================= */
  async function handleCheckSheetsPermission(formData) {
    if (!formData?.title || !formData?.url) {
      notify("Error", "Preencha todos os campos obrigatórios", true);
      return false;
    }

    if (!allVariables.length) {
      notify("Error", "Variáveis ainda estão carregando. Tente novamente.", true);
      return false;
    }

    setLoadingCheck(true);

    try {
      const { token, client_id } = await getTokenData();

      const url =
        process.env.REACT_APP_BACKEND_AWS_URL +
        "/sheets/google/check?url=" +
        encodeURIComponent(formData.url);

      const response = await axios.get(url, {
        headers: {
          "Content-Type": "application/json",
          Authorization: token,
          Client_ID: client_id,
        },
      });

      const example = response?.data?.item_example;

      if (!example) {
        notify(
          "Error",
          "Lembre-se de compartilhar a planilha com o e-mail: planilhas-n8n@projeto-brio-n8n.iam.gserviceaccount.com",
          true
        );
        return false;
      }

      const items = Object.entries(example)
        .filter(([field]) => field !== "row_number")
        .map(([field, value]) => {
          const match = allVariables.find(
            (v) => normalize(v) === normalize(field)
          );

          return {
            field,
            value,
            variable: match || null,
          };
        });

      if (!items.length) {
        notify("Error", "O link não retornou colunas válidas", true);
        return false;
      }

      setDataR(items);
      setPadronizationPage(0);

      return true;
    } catch (error) {
      console.error(error);
      notify(
        "Error",
        "Ocorreu um erro com o link. Verifique se a planilha foi compartilhada corretamente.",
        true
      );
      return false;
    } finally {
      setLoadingCheck(false);
    }
  }

  /* =========================
     Validação antes de finalizar
  ========================= */
  function validateBeforeFinish(formData) {
    if (!formData?.title || !formData?.url) {
      notify("Error", "Preencha todos os campos obrigatórios", true);
      return { valid: false };
    }

    const variables = (dataR || []).map((item) => ({
      field: item.field,
      variable: item.variable || null,
    }));

    const emptyCount = variables.filter((x) => !x.variable).length;

    const hasChannel =
      variables.some((x) => x.variable === "WhatsApp") ||
      variables.some((x) => x.variable === "Email");

    if (!hasChannel) {
      notify("Alert", "É obrigatório selecionar a coluna de WhatsApp ou Email.", true);
      return { valid: false };
    }

    return {
      valid: true,
      emptyCount,
    };
  }

  /* =========================
     Submit final
  ========================= */
  async function handleSubmit({ formData, onSuccess }) {
    try {
      const variables = (dataR || [])
        .filter((item) => item?.field && item?.variable)
        .map((item) => ({
          field: item.field,
          variable: item.variable,
        }));

      const payload = {
        title: formData?.title || "",
        desc: formData?.desc || "",
        url: formData?.url || "",
        formatSheets: formData?.formatSheets || "Google planilhas",
        deleteDuplicatedVariables:
          formData?.deleteDuplicatedVariables || "Não",
        variables
      };

      const response = await PostListagens(payload);

      if (response?.status === "success") {
        setAlreadyFetchSheets(false);
        notify("Success", "Listagem adicionada com sucesso!", true);
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      notify("Error", "Erro ao enviar os dados", true);
    }
  }

  return {
    padronizationPage,
    dataR,
    variablesOfTemplate,
    loadingCheck,

    setPadronizationPage,
    setDataR,

    handleCheckSheetsPermission,
    validateBeforeFinish,
    handleSubmit,
  };
}